import posthog from "posthog-js";
import { scrubUrl } from "./scrubUrl";

// PostHog configuration
// Replace these with your actual PostHog project credentials
const POSTHOG_API_KEY = import.meta.env.VITE_POSTHOG_API_KEY || "";
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || "https://eu.i.posthog.com";

/** Scrubs every string property (and $set/$set_once values) of an event. */
function scrubProperties(props: Record<string, unknown> | undefined) {
	if (!props) return;
	for (const [key, value] of Object.entries(props)) {
		if (typeof value === "string") props[key] = scrubUrl(value);
		else if (value && typeof value === "object" && !Array.isArray(value)) {
			scrubProperties(value as Record<string, unknown>);
		}
	}
}

// Initialize PostHog
export function initPostHog() {
	if (!POSTHOG_API_KEY) {
		console.warn("PostHog API key not configured. Analytics disabled.");
		return;
	}

	posthog.init(POSTHOG_API_KEY, {
		api_host: POSTHOG_HOST,
		// Capture pageviews automatically
		capture_pageview: false, // We'll handle this manually for SPA
		// Capture pageleaves for session duration
		capture_pageleave: true,
		// Respect Do Not Track
		respect_dnt: true,
		// Session recording (optional - enable if needed)
		disable_session_recording: true,
		// Every event carries the current URL; keep checkout session ids out.
		before_send: (event) => {
			if (event) {
				scrubProperties(event.properties);
				scrubProperties(event.$set as Record<string, unknown> | undefined);
				scrubProperties(event.$set_once as Record<string, unknown> | undefined);
			}
			return event;
		},
		// Persistence
		persistence: "localStorage+cookie",
		// Bootstrap on init
		bootstrap: {
			distinctID: undefined,
			isIdentifiedID: false,
		},
		// Autocapture settings
		autocapture: {
			dom_event_allowlist: ["click", "submit"],
			element_allowlist: ["a", "button", "form", "input", "select"],
			css_selector_allowlist: [".btn-primary", ".btn-secondary", "[data-ph-capture]"],
		},
	});
}

// Check if PostHog is initialized
export function isPostHogEnabled(): boolean {
	return !!POSTHOG_API_KEY && typeof posthog !== "undefined";
}

// Track page view
export function trackPageView(path: string, title?: string) {
	if (!isPostHogEnabled()) return;

	posthog.capture("$pageview", {
		$current_url: scrubUrl(window.location.href),
		$pathname: path,
		title: title || document.title,
	});
}

// Track custom events
export function trackEvent(eventName: string, properties?: Record<string, unknown>) {
	if (!isPostHogEnabled()) return;

	posthog.capture(eventName, properties);
}

// Identify user (when they log in or sign up)
export function identifyUser(userId: string, properties?: Record<string, unknown>) {
	if (!isPostHogEnabled()) return;

	posthog.identify(userId, properties);
}

// Reset user (when they log out)
export function resetUser() {
	if (!isPostHogEnabled()) return;

	posthog.reset();
}

// Set user properties without identifying
export function setUserProperties(properties: Record<string, unknown>) {
	if (!isPostHogEnabled()) return;

	posthog.people.set(properties);
}

// Track calculation events
export interface CalculationEventProperties {
	latitude: number;
	longitude: number;
	date?: string;
	year?: number;
	timezone?: string;
	calculationType: "day" | "year";
	success: boolean;
	errorMessage?: string;
}

export function trackCalculation(properties: CalculationEventProperties) {
	trackEvent("calculation_performed", {
		...properties,
		// Round coordinates to protect user privacy (2 decimal places = ~1km precision)
		latitude_rounded: Math.round(properties.latitude * 100) / 100,
		longitude_rounded: Math.round(properties.longitude * 100) / 100,
	});
}

// Track location selection
export interface LocationSelectionProperties {
	method: "quick_location" | "map_click" | "geolocation" | "address_search" | "manual";
	locationName?: string;
	address?: string;
	latitude: number;
	longitude: number;
}

export function trackLocationSelection(properties: LocationSelectionProperties) {
	trackEvent("location_selected", {
		...properties,
		latitude_rounded: Math.round(properties.latitude * 100) / 100,
		longitude_rounded: Math.round(properties.longitude * 100) / 100,
	});
}

// Track CTA clicks
export interface CTAClickProperties {
	ctaName: string;
	ctaLocation: string;
	destination?: string;
}

export function trackCTAClick(properties: CTAClickProperties) {
	trackEvent("cta_clicked", { ...properties });
}

// Track pricing page interactions
export interface PricingEventProperties {
	planViewed?: string;
	planSelected?: string;
	action: "view" | "select" | "contact" | "checkout_started" | "select_without_location";
}

export function trackPricingInteraction(properties: PricingEventProperties) {
	trackEvent("pricing_interaction", { ...properties });
}

// Track feature usage
export interface FeatureUsageProperties {
	feature: string;
	action: string;
	metadata?: Record<string, unknown>;
}

export function trackFeatureUsage(properties: FeatureUsageProperties) {
	trackEvent("feature_used", { ...properties });
}

// Track CSV export
export function trackCSVExport(year: number, latitude: number, longitude: number) {
	trackEvent("csv_exported", {
		year,
		latitude_rounded: Math.round(latitude * 100) / 100,
		longitude_rounded: Math.round(longitude * 100) / 100,
	});
}

export function trackPDFExport(year: number, latitude: number, longitude: number) {
	trackEvent("pdf_exported", {
		year,
		latitude_rounded: Math.round(latitude * 100) / 100,
		longitude_rounded: Math.round(longitude * 100) / 100,
	});
}

// Track demo interaction on landing page
export function trackDemoInteraction(action: "started" | "completed" | "error") {
	trackEvent("demo_interaction", { action });
}

// Track map interaction
export function trackMapInteraction(action: "opened" | "closed" | "clicked") {
	trackEvent("map_interaction", { action });
}

// Track conversion funnel events on the day-view → year-view path
export type ConversionEventName =
	| "day_view_cta_viewed"
	| "day_view_cta_clicked"
	| "preview_scroll_25"
	| "preview_scroll_50"
	| "preview_scroll_75"
	| "preview_scroll_100"
	| "sticky_cta_viewed"
	| "sticky_cta_clicked"
	| "sticky_cta_dismissed"
	| "faq_opened"
	| "teaser_card_viewed"
	| "teaser_card_clicked";

export function trackConversionEvent(name: ConversionEventName, properties?: Record<string, unknown>) {
	trackEvent(name, properties);
}

// Export the posthog instance for advanced use cases
export { posthog };
